import { useEffect, useMemo, useState } from 'react';
import { Dumbbell, Library } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/useAuth';
import { useI18n } from '../../context/useI18n';
import { athleteFirstName, coachSpokenName, summarizeAssignedWeek } from '../../utils/athleteCoachWelcome';

export default function CoachRoutineHomeCard({ onOpenTraining, onOpenGallery }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get('/training/my-current-plan')
      .then((res) => {
        if (!active) return;
        setPlan(res.data?.plan || null);
      })
      .catch(() => {
        if (active) setPlan(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const coach = coachSpokenName({ branding: user?.coach_branding, trainer: user?.trainer });
  const athlete = athleteFirstName(user);
  const weekDays = useMemo(() => summarizeAssignedWeek(plan?.dias), [plan?.dias]);

  if (loading) {
    return (
      <div className="card p-4 animate-pulse space-y-2">
        <div className="h-4 bg-white/10 rounded w-1/3" />
        <div className="h-6 bg-white/10 rounded w-2/3" />
      </div>
    );
  }

  const dias = Array.isArray(plan?.dias) ? plan.dias : [];
  if (!dias.length) {
    return (
      <div className="card p-4 border border-amber-500/30 bg-amber-500/10">
        <p className="text-sm text-[var(--d28d-text)] font-semibold">
          {coach
            ? t('home.routine_pending_named', '{coach} está armando tu rutina', { coach })
            : t('home.routine_pending_title', 'Rutina de tu coach')}
        </p>
        <p className="text-sm d28d-text-muted mt-1">
          {t('home.routine_pending_hint', 'Cuando te la asigne la verás aquí, con las notas para guiarte.')}
        </p>
        {onOpenGallery ? (
          <button type="button" className="btn btn-secondary text-sm mt-3" onClick={onOpenGallery}>
            {t('home.open_gallery', 'Abrir biblioteca')}
          </button>
        ) : null}
      </div>
    );
  }

  const hello = athlete ? `Hola ${athlete}` : 'Hola';
  const soy = coach ? `soy ${coach}, tu entrenador` : 'soy tu entrenador';

  return (
    <div className="card p-4 space-y-3 border border-lime-500/25">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-lime-500/15 flex items-center justify-center shrink-0">
          <Dumbbell className="w-5 h-5 text-lime-400" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-[var(--d28d-text)] leading-snug m-0">
            {hello}, {soy}.
          </p>
          <p className="text-sm d28d-text-muted mt-1 mb-0">
            Para esta semana esta es la rutina que te asigné, para que cumplamos nuestras metas. Entrenaremos así:
          </p>
        </div>
      </div>
      {weekDays.length > 0 ? (
        <ul className="text-sm space-y-1 pl-1 m-0">
          {weekDays.slice(0, 7).map((row) => (
            <li key={row.dia} className="text-[var(--d28d-text)] opacity-90">
              · {row.label}
              {row.hasNotes ? ' · te dejé notas' : ''}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="text-sm d28d-text-muted m-0">
        En cada día te puedo guiar en las notas. Abre tu rutina, lee y registra lo que hagas: es fácil.
      </p>
      <div className="flex flex-wrap gap-2 pt-1">
        <button type="button" className="btn btn-primary text-sm" onClick={onOpenTraining}>
          {t('home.see_my_routine', 'Ver mi rutina')}
        </button>
        {onOpenGallery ? (
          <button type="button" className="btn btn-secondary text-sm inline-flex items-center gap-1.5" onClick={onOpenGallery}>
            <Library className="w-4 h-4" />
            {t('home.open_gallery', 'Abrir biblioteca')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
